import React from "react";
import { ConsensusEventMessage } from "../ExplorerTestPage";
import {
  ConsensusEventFlat,
  ConsensusVertexItemFlat,
  ConsensusTxItemFlat,
  ConsensusUnconfirmedTxItemFlat,
  ConsensusValidatorItemFlat,
  TransactionEvent,
} from "../../sdk/types/api";

interface ConsensusEventItemProps {
  message: ConsensusEventMessage;
}

export const ConsensusEventItem: React.FC<ConsensusEventItemProps> = ({
  message,
}) => {
  const { event, timestamp, type, eventSource } = message;

  // Check if this is a TransactionEvent (from transactions channel)
  const isTransactionEvent = (e: ConsensusEventFlat | TransactionEvent): e is TransactionEvent => {
    return "data" in e && e.data && typeof e.data === "object" && ("hash" in e.data || "txhash" in e.data);
  };

  // Check if this is a ConsensusEventFlat (from consensus_flat channel)
  const isConsensusEventFlat = (e: ConsensusEventFlat | TransactionEvent): e is ConsensusEventFlat => {
    return "data" in e && e.data && typeof e.data === "object" && "type" in e.data;
  };

  const renderEventData = () => {
    if (isTransactionEvent(event)) {
      return <TransactionEventDetails event={event} />;
    }

    if (isConsensusEventFlat(event)) {
      const { data } = event;
      switch (data.type) {
        case "vertex":
          return <VertexDetails data={data as ConsensusVertexItemFlat} />;
        case "tx":
          return <TransactionDetails data={data as ConsensusTxItemFlat} />;
        case "unconfirmed_tx":
          return (
            <UnconfirmedTransactionDetails
              data={data as ConsensusUnconfirmedTxItemFlat}
            />
          );
        case "validator":
          return <ValidatorDetails data={data as ConsensusValidatorItemFlat} />;
        default:
          return <GenericDetails data={data} />;
      }
    }

    return <GenericDetails data={event} />;
  };

  const sourceShard = isTransactionEvent(event) 
    ? event.source_shard || "unknown"
    : event.source_shard;

  return (
    <div className={`consensus-event-item ${type}`}>
      <div className="event-header">
        <div className="event-meta">
          <span className="event-type-badge">{event.event_type}</span>
          {eventSource && (
            <span className="event-source-badge">{eventSource}</span>
          )}
          <span className="event-time">
            {new Date(timestamp).toLocaleString()}
          </span>
          <span className="event-shard">Shard: {sourceShard}</span>
        </div>
      </div>
      <div className="event-body">
        {isConsensusEventFlat(event) && (
          <div className="event-data-type">
            <strong>Data Type:</strong> {event.data.type}
          </div>
        )}
        {renderEventData()}
        <details className="raw-data-details">
          <summary>View Raw JSON</summary>
          <pre className="raw-data-pre">
            {JSON.stringify(event, null, 2)}
          </pre>
        </details>
      </div>
    </div>
  );
};

const VertexDetails: React.FC<{ data: ConsensusVertexItemFlat }> = ({
  data,
}) => {
  return (
    <div className="event-details vertex-details">
      <div className="detail-row">
        <span className="detail-label">ID:</span>
        <span className="detail-value">{data.id}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Shard ID:</span>
        <span className="detail-value">{data.shard_id}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Status:</span>
        <span className="detail-value">{data.status}</span>
      </div>
      {data.parent_ids && data.parent_ids.length > 0 && (
        <div className="detail-row">
          <span className="detail-label">Parent IDs:</span>
          <span className="detail-value">
            {data.parent_ids.join(", ")}
          </span>
        </div>
      )}
      {data.tx_count !== undefined && (
        <div className="detail-row">
          <span className="detail-label">TX Count:</span>
          <span className="detail-value">{data.tx_count}</span>
        </div>
      )}
      {data.state_root && (
        <div className="detail-row">
          <span className="detail-label">State Root:</span>
          <span className="detail-value">{data.state_root}</span>
        </div>
      )}
      {data.qc_achieved !== undefined && (
        <div className="detail-row">
          <span className="detail-label">QC Achieved:</span>
          <span className="detail-value">
            {data.qc_achieved ? "Yes" : "No"}
          </span>
        </div>
      )}
      {data.qc_vote_count !== undefined && (
        <div className="detail-row">
          <span className="detail-label">QC Vote Count:</span>
          <span className="detail-value">{data.qc_vote_count}</span>
        </div>
      )}
      <div className="detail-row">
        <span className="detail-label">Timestamp:</span>
        <span className="detail-value">
          {new Date(data.timestamp * 1000).toLocaleString()}
        </span>
      </div>
    </div>
  );
};

const TransactionDetails: React.FC<{ data: ConsensusTxItemFlat }> = ({
  data,
}) => {
  return (
    <div className="event-details tx-details">
      <div className="detail-row">
        <span className="detail-label">TX Hash:</span>
        <span className="detail-value">{data.txhash || data.id}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Shard ID:</span>
        <span className="detail-value">{data.shard_id}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Status:</span>
        <span className="detail-value">{data.status}</span>
      </div>
      {data.from_address && (
        <div className="detail-row">
          <span className="detail-label">From:</span>
          <span className="detail-value">{data.from_address}</span>
        </div>
      )}
      {data.to_address && (
        <div className="detail-row">
          <span className="detail-label">To:</span>
          <span className="detail-value">{data.to_address}</span>
        </div>
      )}
      {data.amount && (
        <div className="detail-row">
          <span className="detail-label">Amount:</span>
          <span className="detail-value">{data.amount}</span>
        </div>
      )}
      {data.tx_type && (
        <div className="detail-row">
          <span className="detail-label">TX Type:</span>
          <span className="detail-value">{data.tx_type}</span>
        </div>
      )}
      {data.nonce !== undefined && (
        <div className="detail-row">
          <span className="detail-label">Nonce:</span>
          <span className="detail-value">{data.nonce}</span>
        </div>
      )}
      <div className="detail-row">
        <span className="detail-label">Timestamp:</span>
        <span className="detail-value">
          {new Date(data.timestamp * 1000).toLocaleString()}
        </span>
      </div>
    </div>
  );
};

const UnconfirmedTransactionDetails: React.FC<{
  data: ConsensusUnconfirmedTxItemFlat;
}> = ({ data }) => {
  return (
    <div className="event-details unconfirmed-tx-details">
      <div className="detail-row">
        <span className="detail-label">TX Hash:</span>
        <span className="detail-value">{data.txhash || data.id}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Status:</span>
        <span className="detail-value status-pending">{data.status}</span>
      </div>
      {data.from_address && (
        <div className="detail-row">
          <span className="detail-label">From:</span>
          <span className="detail-value">{data.from_address}</span>
        </div>
      )}
      {data.to_address && (
        <div className="detail-row">
          <span className="detail-label">To:</span>
          <span className="detail-value">{data.to_address}</span>
        </div>
      )}
      {data.amount && (
        <div className="detail-row">
          <span className="detail-label">Amount:</span>
          <span className="detail-value">{data.amount}</span>
        </div>
      )}
      <div className="detail-row">
        <span className="detail-label">Timestamp:</span>
        <span className="detail-value">
          {new Date(data.timestamp * 1000).toLocaleString()}
        </span>
      </div>
    </div>
  );
};

const ValidatorDetails: React.FC<{ data: ConsensusValidatorItemFlat }> = ({
  data,
}) => {
  return (
    <div className="event-details validator-details">
      <div className="detail-row">
        <span className="detail-label">Validator ID:</span>
        <span className="detail-value">{data.validator_id || data.id}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Shard ID:</span>
        <span className="detail-value">{data.shard_id}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Status:</span>
        <span className="detail-value">{data.validator_status || data.status}</span>
      </div>
      {data.voting_power !== undefined && (
        <div className="detail-row">
          <span className="detail-label">Voting Power:</span>
          <span className="detail-value">{data.voting_power}</span>
        </div>
      )}
      {data.public_key && (
        <div className="detail-row">
          <span className="detail-label">Public Key:</span>
          <span className="detail-value public-key">{data.public_key}</span>
        </div>
      )}
      <div className="detail-row">
        <span className="detail-label">Timestamp:</span>
        <span className="detail-value">
          {new Date(data.timestamp * 1000).toLocaleString()}
        </span>
      </div>
    </div>
  );
};

const TransactionEventDetails: React.FC<{ event: TransactionEvent }> = ({
  event,
}) => {
  const { data } = event;
  return (
    <div className="event-details transaction-event-details">
      <div className="detail-row">
        <span className="detail-label">Hash:</span>
        <span className="detail-value">{data.hash || data.txhash || "N/A"}</span>
      </div>
      {data.vertex_hash && (
        <div className="detail-row">
          <span className="detail-label">Vertex Hash:</span>
          <span className="detail-value">{data.vertex_hash}</span>
        </div>
      )}
      {data.status && (
        <div className="detail-row">
          <span className="detail-label">Status:</span>
          <span className="detail-value">{data.status}</span>
        </div>
      )}
      {data.shard_id !== undefined && (
        <div className="detail-row">
          <span className="detail-label">Shard ID:</span>
          <span className="detail-value">{data.shard_id}</span>
        </div>
      )}
      {data.dag_depth !== undefined && (
        <div className="detail-row">
          <span className="detail-label">DAG Depth:</span>
          <span className="detail-value">{data.dag_depth}</span>
        </div>
      )}
      {data.consensus_round !== undefined && (
        <div className="detail-row">
          <span className="detail-label">Consensus Round:</span>
          <span className="detail-value">{data.consensus_round}</span>
        </div>
      )}
      {data.code !== undefined && (
        <div className="detail-row">
          <span className="detail-label">Code:</span>
          <span className="detail-value">{data.code}</span>
        </div>
      )}
      {data.info && (
        <div className="detail-row">
          <span className="detail-label">Info:</span>
          <span className="detail-value">{data.info}</span>
        </div>
      )}
      {data.raw_log && (
        <div className="detail-row">
          <span className="detail-label">Raw Log:</span>
          <span className="detail-value">{data.raw_log}</span>
        </div>
      )}
      {data.transaction_index !== undefined && (
        <div className="detail-row">
          <span className="detail-label">Transaction Index:</span>
          <span className="detail-value">{data.transaction_index}</span>
        </div>
      )}
      {data.consensus_version && (
        <div className="detail-row">
          <span className="detail-label">Consensus Version:</span>
          <span className="detail-value">{data.consensus_version}</span>
        </div>
      )}
      {data.parent_vertices && data.parent_vertices.length > 0 && (
        <div className="detail-row">
          <span className="detail-label">Parent Vertices:</span>
          <span className="detail-value">
            {data.parent_vertices.join(", ")}
          </span>
        </div>
      )}
      {event.event_time && (
        <div className="detail-row">
          <span className="detail-label">Event Time:</span>
          <span className="detail-value">
            {typeof event.event_time === "number"
              ? new Date(event.event_time * 1000).toLocaleString()
              : typeof event.event_time === "object" && "seconds" in event.event_time
                ? new Date(event.event_time.seconds * 1000).toLocaleString()
                : "N/A"}
          </span>
        </div>
      )}
    </div>
  );
};

const GenericDetails: React.FC<{ data: any }> = ({ data }) => {
  return (
    <div className="event-details generic-details">
      <div className="detail-row">
        <span className="detail-label">ID:</span>
        <span className="detail-value">{data.id}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Type:</span>
        <span className="detail-value">{data.type}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Status:</span>
        <span className="detail-value">{data.status}</span>
      </div>
    </div>
  );
};

